import React, {useState} from "react";
import {Avatar} from "@/components/avatar/Avatar";
import {
    MdOutlineThumbDown,
    MdOutlineThumbUp,
    MdThumbDown,
    MdThumbUp
} from "react-icons/md";
import {MessageStore} from "@/services/models/messageStore";
import LoadingAnimation from "@/app/components/custom-loading";
import EON from '@/public/Image_Editor.png';
import MarkdownRenderer from './chat-renderer/markdown-renderer';
import {usePrTheme} from "@/app/components/themeProvider/pr-theme-provider";
import HoverWrapper from "@/app/components/hover-component";
import {chatApiActions} from "@/store/features/chat/chatApiSlice";
import {FiCheck, FiCopy} from "react-icons/fi";
import {EONThinking} from "@/app/components/eon-thinking";

interface AiMessageProps {
    content: MessageStore & {
        hasIframe?: boolean;
        iframeContent?: string;
    };
    isVotingAvailable: boolean;
    onDisplayIframe?: (messageId: number) => void;
    displayedIframeId?: number | null;
    useVoteMutation?: any;
    showAvatar?: boolean;
    compactMobileSpacing?: boolean;
}

const AiMessage = React.memo(({content, isVotingAvailable, onDisplayIframe, displayedIframeId, useVoteMutation, showAvatar = true, compactMobileSpacing = false}: AiMessageProps) => {
    const [hasCopied, setHasCopied] = useState(false);
    const [localVote, setLocalVote] = useState(content.vote);
    const [postVote] = useVoteMutation ? useVoteMutation() : chatApiActions.usePostVoteMutation();
    const theme = usePrTheme();


    const handleCopy = async () => {
        try {
            // Copy the text content (without iframe)
            await navigator.clipboard.writeText(content.message);
            setHasCopied(true);
            setTimeout(() => {
                setHasCopied(false);
            }, 2000);
        } catch (err) {
            console.error('Failed to copy text:', err);
        }
    };

    const handleVote = async (rating: number) => {
        const previousVote = localVote;
        setLocalVote(rating === localVote ? 0 : rating);

        try {
            await postVote({
                messageId: content.id,
                vote: rating === localVote ? 0 : rating
            }).unwrap();
        } catch (err) {
            setLocalVote(previousVote);
            console.error('Failed to vote:', err);
        }
    };

    const hasValidId = content.id !== -1;
    const showButtons = !content.isLoading && hasValidId && isVotingAvailable;
    const hasIframe = content.hasIframe;
    const isCurrentlyDisplayed = displayedIframeId === content.id;

    const handleDisplayWidget = () => {
        if (onDisplayIframe && hasIframe) {
            onDisplayIframe(content.id);
        }
    };

    const hasToolExecutions = content.toolExecutions && content.toolExecutions.length > 0;
    const hasActiveToolExecutions = content.toolExecutions?.some(te => te.status === 'calling') || false;
    
    // Show spinner while loading OR while there are active tool executions
    // Spinner disappears only when isLoading is false AND no active tool executions
    const shouldShowSpinner = content.isLoading || hasActiveToolExecutions;
    const shouldShowContent = !content.isLoading || hasToolExecutions;
    const shouldShowAvatar = showAvatar && !shouldShowSpinner; // Don't show avatar when showing spinner

    return (
        <div className={`max-w-[768px] w-full ${compactMobileSpacing ? 'p-2 md:p-4' : 'p-4'}`}>
            <div className={`flex items-start ${compactMobileSpacing ? 'space-x-1 md:space-x-6' : 'space-x-6'} w-full`}>
                {shouldShowAvatar && (
                    <div className="flex-shrink-0 z-0">
                        <Avatar size={50} src={EON.src}/>
                    </div>
                )}
                {shouldShowSpinner && (
                    <div className="flex-shrink-0 z-0">
                        <LoadingAnimation
                            size="sm"
                            containerClassName="-mt-1"
                        />
                    </div>
                )}
                {shouldShowContent && (
                    <div className="flex-1 min-w-0 w-full overflow-hidden">
                        {/* EON Thinking component - shows tool executions - appears FIRST */}
                        {/* Show even when isLoading is true if there are tool executions */}
                        {hasToolExecutions && content.toolExecutions && (
                            <EONThinking 
                                toolExecutions={content.toolExecutions} 
                                compactMobileSpacing={compactMobileSpacing}
                            />
                        )}
                        {/* Only show message content when not loading OR when there's already content */}
                        {(!content.isLoading || content.message) && (
                            <div className="text-gray-700 text-left w-full overflow-x-auto">
                                {/* Show only the text content, iframe is displayed in the right panel */}
                                {content.message && (
                                    <MarkdownRenderer content={content.message}/>
                                )}
                                {hasIframe && (
                                    <div className="mt-3">
                                        <button
                                            onClick={handleDisplayWidget}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                isCurrentlyDisplayed
                                                    ? 'bg-blue-100 text-blue-800 border border-blue-300'
                                                    : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                                            }`}
                                        >
                                            {isCurrentlyDisplayed ? '✓ Widget Displayed' : '📊 Display Widget'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        {showButtons && (
                            <div className="mt-4">
                                <ButtonGroup
                                    hasCopied={hasCopied}
                                    handleCopy={handleCopy}
                                    vote={localVote}
                                    handleVote={handleVote}
                                    color={'4B5563'}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    // Custom comparison function to ensure re-render when toolExecutions change
    const prevToolExecutions = prevProps.content.toolExecutions || [];
    const nextToolExecutions = nextProps.content.toolExecutions || [];
    
    // Always re-render if toolExecutions exist and changed (simplified comparison)
    // Compare by creating a string representation using toolCallId for uniqueness
    const prevKey = JSON.stringify(prevToolExecutions.map(te => ({ toolCallId: te.toolCallId, toolName: te.toolName, status: te.status, timestamp: te.timestamp })));
    const nextKey = JSON.stringify(nextToolExecutions.map(te => ({ toolCallId: te.toolCallId, toolName: te.toolName, status: te.status, timestamp: te.timestamp })));
    
    if (prevKey !== nextKey) {
        return false; // false means props changed, should re-render
    }
    
    // Check other props for changes
    const otherPropsEqual = (
        prevProps.content.id === nextProps.content.id &&
        prevProps.content.message === nextProps.content.message &&
        prevProps.content.isLoading === nextProps.content.isLoading &&
        prevProps.content.vote === nextProps.content.vote &&
        prevProps.isVotingAvailable === nextProps.isVotingAvailable &&
        prevProps.displayedIframeId === nextProps.displayedIframeId &&
        prevProps.showAvatar === nextProps.showAvatar &&
        prevProps.compactMobileSpacing === nextProps.compactMobileSpacing
    );
    
    if (!otherPropsEqual) {
        return false;
    }
    
    return true; // true means props are equal, skip re-render
});

const ButtonGroup = React.memo(({hasCopied, handleCopy, vote, handleVote, color}: {
    hasCopied: boolean;
    vote: number;
    handleCopy: () => void;
    handleVote: (rating: number) => void;
    color: string;
}) => (
    <div className="flex space-x-6">
        <button
            className="flex items-center text-gray-500 hover:text-gray-700"
            onClick={handleCopy}
            aria-label="Copy message"
        >
            {hasCopied ? (
                <HoverWrapper tooltipPosition={"bottom"} tooltipText={"Copied"}>
                    <FiCheck className="w-5 h-5" style={{color: `#${color}`}}/>
                </HoverWrapper>
            ) : (
                <HoverWrapper tooltipPosition={"bottom"} tooltipText={"Copy"}>
                    <FiCopy className="w-5 h-5" style={{color: `#${color}`}}/>
                </HoverWrapper>

            )}
        </button>
        <button
            className="flex items-center text-gray-500 hover:text-gray-700"
            onClick={() => handleVote(1)}
            aria-label="Vote up"
        >
            {vote === 1 ? (
                <HoverWrapper tooltipPosition={"bottom"} tooltipText={"Liked"}>
                    <MdThumbUp className="w-5 h-5" style={{color: `#${color}`}}/>
                </HoverWrapper>
            ) : (
                <HoverWrapper tooltipPosition={"bottom"} tooltipText={"Like"}>
                    <MdOutlineThumbUp className="w-5 h-5" style={{color: `#${color}`}}/>
                </HoverWrapper>
            )}
        </button>
        <button
            className="flex items-center text-gray-500 hover:text-gray-700"
            onClick={() => handleVote(-1)}
            aria-label="Vote down"
        >
            {vote === -1 ? (
                <HoverWrapper tooltipPosition={"bottom"} tooltipText={"Disliked"}>
                    <MdThumbDown className="w-5 h-5" style={{color: `#${color}`}}/>
                </HoverWrapper>
            ) : (
                <HoverWrapper tooltipPosition={"bottom"} tooltipText={"Dislike"}>
                    <MdOutlineThumbDown className="w-5 h-5" style={{color: `#${color}`}}/>
                </HoverWrapper>
            )}
        </button>
    </div>
));

export default AiMessage;